/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AdyenCheckout, Ach, type AchConfiguration } from '@adyen/adyen-web';
import '@adyen/adyen-web/styles/adyen.css';
import {
  Alert,
  AlertTypography,
  AppHeaderBackButton,
  Box,
  Checkbox,
  CheckboxGroup,
  CheckboxTypography,
  Icon,
  Link,
  Modal,
  ModalActionButton,
  ModalActions,
  ModalCloseIconButton,
  ModalContent,
  ModalTitle,
  StickyButton,
  Typography,
  useNameOnCardValidator,
} from '@ace-clublabs/shared-component-library';
import { LogCannon } from '@ace-clublabs/log-cannon';
import {
  ErrorCode,
  IConfigEntry,
  IProcessPaymentWithManifestRequest,
  IAdyenEncryptedAchData,
  PaymentType,
} from '@ace-clublabs/payment-types';
import { getMyAccountURL } from '@ace-clublabs/payments-helper';
import { useNavigate } from 'react-router-dom';
import echeckImage from '../assets/echeck-find.png';
import { PaymentReview } from '../containers/PaymentReview';
import { Pages } from '../shared/pages';
import { navigatePaymentFormBack } from '../utils/paymentNavigationHelper';
import { MANIFEST_TTL_MS } from '../constants';
import { useGlobalState } from '../shared/state';
import { ErrorSeverity, ErrorType } from '../shared/types';
import { getTermsUrl } from '../utils/methods';
import { ProcessPaymentHelper } from '../utils/processPaymentHelper';
import UrlHelper from '../utils/urlHelper';
import Header from './Header';
import PaymentHeader from './PaymentHeader';
import { PaymentMethodsAuthorizationModal } from './PaymentMethodsAuthorizationModal';
import '../styles/override.css';
import { sources } from '../api/sources';
import { IGetAdyenSession, ISubmitAdyenTokenRequest } from '../api/contracts';
import { installAdyenMobileClearButtons } from '../utils/installAdyenMobileClearButtons';

export interface ECheckFormProps {
  manifestId: string;
  amount: number;
  returnUrl: string;
  isScheduled: boolean | undefined;
  autoPay?: boolean;
  appId?: string;
  scheduledDate?: string;
  paymentFormSubtitle?: string;
  setParentProcessing?: any;
  hideWhileParentProcessing?: boolean;
  tokenOnly?: boolean;
  softErrorCode?: ErrorCode;
}

/** Shown on payment review when routing is not available from the SDK. */
const ROUTING_REVIEW_PLACEHOLDER = '·········';

const ACCOUNT_RETYPE_MISMATCH_MESSAGE = 'Account number does not match.';
const ACCOUNT_LENGTH_MIN_MESSAGE = 'Account number should be at least 5 digits.';

/**
 * Adyen Web ACH uses one `selectedAccountType` value (`personal.checking`, etc.) for both
 * account holder type and bank account type. Types only document `ownerName` prefill, but the
 * runtime merges this into form state. We hide the selector in CSS and default here.
 */
const ADYEN_ACH_DEFAULT_SELECTED_ACCOUNT_TYPE = 'personal.checking';

/** All ACH `data-id` values (labels present in the Adyen form). */
const ACH_FIELD_DATA_IDS = ['ownerName', 'routingNumber', 'accountNumber', 'accountNumberVerification'] as const;

/** Insert “Where do I find this?” for routing + account only. */
const ACH_HELP_FIELD_NAMES: readonly (typeof ACH_FIELD_DATA_IDS)[number][] = ['routingNumber', 'accountNumber'];

function achPayloadFromPaymentMethod(pm: any): IAdyenEncryptedAchData | null {
  if (!pm) return null;
  const accountNumber = pm.accountNumber ?? pm.bankAccountNumber;
  const routingNumber = pm.routingNumber ?? pm.bankLocationId;
  if (!accountNumber || !routingNumber) return null;

  return {
    ownerName: String(pm.ownerName || '').trim(),
    accountNumber: String(accountNumber),
    routingNumber: String(routingNumber),
    bankAccountType: String(pm.bankAccountType || 'checking'),
    accountHolderType: String(pm.accountHolderType || 'personal'),
    last4: pm.bankAccountNumberLastDigits || pm.lastFourDigits || pm.last4,
  };
}

/**
 * Re-type (verification) must equal the account number. Align with ECheckForm `confirmMismatch` where
 * it helps, and catch early when re-type is clearly “account + extra digits” (e.g. `123` / `1233`) which
 * can never match as a whole value.
 */
function achAccountFieldsMismatchFromDom(): boolean {
  const root = document.getElementById('ach-container');
  if (!root) return false;
  const acc = root.querySelector('input[name="accountNumber"]') as HTMLInputElement | null;
  const conf = root.querySelector('input[name="accountNumberVerification"]') as HTMLInputElement | null;
  if (!acc || !conf) return false;
  const a = acc.value.replace(/\D/g, '');
  const c = conf.value.replace(/\D/g, '');
  if (a.length < 1 || c.length < 1) return false;
  if (a === c) return false;
  if (c.length >= 5 && a !== c) {
    return true;
  }
  if (a.length >= 3 && a.length < c.length && c.startsWith(a)) {
    return true;
  }
  return false;
}

/**
 * "Account number does not match" — re-check on every relevant input, not only when Adyen flips `isValid`.
 * Prefer DOM (ECheckForm rules); otherwise use per-field `valid` from the ACH state if present.
 */
function achAccountMismatch(achState: any): boolean {
  if (achAccountFieldsMismatchFromDom()) return true;
  if (!achState) return false;
  const v = achState.valid;
  if (v && typeof v.encryptedBankAccountNumberVerification === 'boolean') {
    return !v.encryptedBankAccountNumberVerification;
  }
  return false;
}

/** If Adyen already shows a *visible* field error in the re-type box, do not add our same message (avoids two “do not match” lines). */
function adyenHasNativeRetypeContextualError(fieldEl: HTMLElement | null): boolean {
  if (!fieldEl) return false;
  for (const el of fieldEl.querySelectorAll<HTMLElement>(
    '.adyen-checkout-contextual-text--error:not([data-ach-retype-mismatch])'
  )) {
    if (el.textContent?.trim()) return true;
  }
  return false;
}

/**
 * E-check via Adyen Web `Ach` component (session checkout).
 * Requires the Adyen session to be created with ACH available as a payment method.
 */
const ECheckFormAdyen: React.FC<ECheckFormProps> = (props) => {
  const navigate = useNavigate();
  const {
    manifestId,
    amount,
    returnUrl,
    autoPay,
    appId,
    scheduledDate,
    isScheduled,
    paymentFormSubtitle,
    setParentProcessing,
    hideWhileParentProcessing = false,
    tokenOnly,
    softErrorCode,
  } = props;

  const [manifest] = useGlobalState('manifest');
  const [redirectMap, setRedirected] = useGlobalState('redirectMap');
  const [, setPaymentProcessing] = useGlobalState('paymentProcessing');
  const [isMobileApp] = useGlobalState('isMobileApp');
  const [paymentMethodsSkipped, setpaymentMethodsSkipped] = useGlobalState('paymentMethodsSkipped');
  const [, setError] = useGlobalState('error') || [];
  const [manifestTimestamp] = useGlobalState('manifestTimestamp') || [];
  const [adyenSession, setAdyenSession] = useState<IGetAdyenSession | null>(null);
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [isProcessing, setProcessing] = useState(false);
  const [isPaymentReview, setPaymentReview] = useState(false);
  const [last4, setLast4] = useState('');
  const [routingNumber, setRoutingNumber] = useState(ROUTING_REVIEW_PLACEHOLDER);
  const [showModal, setShowModal] = useState(false);
  const [showEcheckModal, setShowEcheckModal] = useState(false);
  const config = manifest?.clientConfig || ({} as IConfigEntry);
  const [storePaymentChecked, setStorePaymentChecked] = useState(false);
  const [reviewPayload, setReviewPayload] = useState<any>(undefined);
  const [showPaymentAuthorization, setShowPaymentAuthorization] = useState(false);
  const [achComponent, setAchComponent] = useState<any>(null);
  const nameOnAccountValidator = useNameOnCardValidator({ maxLength: 26 });
  const [isFormValid, setIsFormValid] = useState(false);
  const [capturedAchData, setCapturedAchData] = useState<IAdyenEncryptedAchData | null>(null);
  const [achApiError, setAchApiError] = useState('');
  const [paymentStatusUnknown, setPaymentStatusUnknown] = useState(false);
  const [accountRetypeFieldEl, setAccountRetypeFieldEl] = useState<HTMLElement | null>(null);
  const [accountRetypeMismatchError, setAccountRetypeMismatchError] = useState<string | null>(null);
  /** When Adyen already renders a re-type error node, we skip our portaled line (same words, different punctuation). */
  const [showPortaledRetypeMismatch, setShowPortaledRetypeMismatch] = useState(true);
  const [accountNumberFieldEl, setAccountNumberFieldEl] = useState<HTMLElement | null>(null);
  const [accountLengthError, setAccountLengthError] = useState<string | null>(null);

  const adyenInitializedRef = useRef(false);
  const latestAchStateRef = useRef<any>(null);
  const accountValueRef = useRef('');
  const retypeValueRef = useRef('');

  const syncButtonValidation = useCallback((s: any) => {
    if (!s) return;
    const pm = s?.data?.paymentMethod;
    const encryptedPayload = pm ? achPayloadFromPaymentMethod(pm) : null;
    const adyenFormValid = !!s?.isValid && !!encryptedPayload;
    const root = document.getElementById('ach-container');
    const accEl = root?.querySelector('input[name="accountNumber"]') as HTMLInputElement | null;
    const confEl = root?.querySelector('input[name="accountNumberVerification"]') as HTMLInputElement | null;
    const a = (accEl?.value ?? accountValueRef.current).replace(/\D/g, '');
    const c = (confEl?.value ?? retypeValueRef.current).replace(/\D/g, '');
    const domMismatch = a.length > 0 && c.length > 0 && a !== c;
    const tooShort = a.length > 0 && a.length < 5;
    const canUse = adyenFormValid && !domMismatch && !tooShort;
    setIsFormValid(canUse);
    if (canUse && pm) {
      const payload = achPayloadFromPaymentMethod(pm);
      if (payload?.ownerName) {
        setCapturedAchData(payload);
        if (payload.accountNumber || payload.last4) {
          setLast4(String(payload.last4 || payload.accountNumber.substring(payload.accountNumber.length - 4)));
        }
        if (payload.routingNumber) setRoutingNumber(String(payload.routingNumber));
      }
    } else {
      setCapturedAchData(null);
    }
  }, []);

  const syncMismatchError = useCallback((s: any) => {
    const mismatch = achAccountMismatch(s);
    setAccountRetypeMismatchError(mismatch ? ACCOUNT_RETYPE_MISMATCH_MESSAGE : null);
  }, []);

  useLayoutEffect(() => {
    if (!achComponent) return;
    const container = document.getElementById('ach-container');
    if (!container) return;

    /** Keep help: input → contextual text (Adyen errors/hints) → “Where do I find this?” */
    const positionAchFieldHelp = (field: Element, wrap: Element) => {
      const inputWrapper = field.querySelector('.adyen-checkout__input-wrapper');
      if (!inputWrapper) {
        field.appendChild(wrap);
        return;
      }
      const contextuals = [...field.querySelectorAll('.adyen-checkout-contextual-text')].filter(
        (el) => inputWrapper.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING
      );
      const anchor = contextuals.length > 0 ? contextuals[contextuals.length - 1]! : inputWrapper;
      if (wrap.parentElement === anchor.parentElement && wrap.previousElementSibling === anchor) return;
      anchor.insertAdjacentElement('afterend', wrap);
    };

    const repositionAchHelps = () => {
      for (const name of ACH_HELP_FIELD_NAMES) {
        const label = container.querySelector(`span.adyen-checkout__label__text[data-id="${name}"]`);
        const field = label?.closest('.adyen-checkout__field');
        const wrap = field?.querySelector('.echeck-adyen-field-help');
        if (field && wrap) positionAchFieldHelp(field, wrap);
      }
    };

    const attachHelpLinks = () => {
      container.querySelectorAll('.echeck-adyen-field-help').forEach((el) => el.remove());
      for (const name of ACH_HELP_FIELD_NAMES) {
        const label = container.querySelector(`span.adyen-checkout__label__text[data-id="${name}"]`);
        const field = label?.closest('.adyen-checkout__field');
        if (!field) continue;
        const wrap = document.createElement('div');
        wrap.className = 'echeck-adyen-field-help';
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.id = name === 'routingNumber' ? 'echeck-adyen-routing-help-link' : 'echeck-adyen-account-help-link';
        btn.className = 'echeck-adyen-field-help__btn';
        btn.textContent = 'Where do I find this?';
        btn.addEventListener('click', (ev) => {
          ev.preventDefault();
          setShowEcheckModal(true);
        });
        wrap.appendChild(btn);
        /* Inside the field so `.adyen-checkout__fieldset__fields` row-gap does not treat the link as its own row. */
        positionAchFieldHelp(field, wrap);
      }
    };

    const fieldsPresent = () =>
      ACH_FIELD_DATA_IDS.every((name) =>
        container.querySelector(`span.adyen-checkout__label__text[data-id="${name}"]`)
      );

    const cleanupHelps = () => {
      container.querySelectorAll('.echeck-adyen-field-help').forEach((el) => el.remove());
    };

    let layoutMo: MutationObserver | null = null;
    const startLayoutObserver = () => {
      if (layoutMo) return;
      layoutMo = new MutationObserver(() => {
        repositionAchHelps();
      });
      layoutMo.observe(container, { childList: true, subtree: true });
    };
    const stopLayoutObserver = () => {
      layoutMo?.disconnect();
      layoutMo = null;
    };

    if (fieldsPresent()) {
      attachHelpLinks();
      startLayoutObserver();
      return () => {
        stopLayoutObserver();
        cleanupHelps();
      };
    }

    const mo = new MutationObserver(() => {
      if (fieldsPresent()) {
        attachHelpLinks();
        startLayoutObserver();
        mo.disconnect();
      }
    });
    mo.observe(container, { childList: true, subtree: true });

    return () => {
      mo.disconnect();
      stopLayoutObserver();
      cleanupHelps();
    };
  }, [achComponent]);

  const allowStoredPayment = config?.allowAdyenStoredPayment;

  const adyenSessionConfig = useMemo(() => {
    return adyenSession
      ? {
          id: adyenSession.adyen.sessionId,
          sessionData: adyenSession.adyen.sessionData,
        }
      : undefined;
  }, [adyenSession?.adyen.sessionId, adyenSession?.adyen.sessionData]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        setIsLoadingSession(true);
        const sessionData = await sources.getAdyenSession(manifestId, { paymentType: PaymentType.Echeck });
        if (isMounted) setAdyenSession(sessionData);
      } catch (error) {
        let errorMessage = 'ERROR: Failed to fetch Adyen session (e-check)';
        if (isMounted) {
          setParentProcessing?.({ status: false });
          LogCannon.blast('Navigating to Error', {
            appId,
            component: 'ECheckFormAdyen',
            errorMessage,
            error: typeof error === 'string' ? `${error}` : error,
            manifestId,
          });
          navigate(Pages.Error);
        }
      } finally {
        if (isMounted) setIsLoadingSession(false);
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, [manifestId, navigate, setParentProcessing]);

  useEffect(() => {
    adyenInitializedRef.current = false;
  }, [adyenSessionConfig]);

  useEffect(() => {
    if (!adyenSessionConfig || !adyenSession) return;
    if (adyenInitializedRef.current) return;
    adyenInitializedRef.current = true;

    let isMounted = true;
    let ach: any = null;

    const init = async () => {
      try {
        let loadingHidden = false;
        const hideLoading = () => {
          if (!loadingHidden && isMounted) {
            loadingHidden = true;
            setTimeout(() => {
              if (isMounted) {
                setParentProcessing?.({ status: false });
              }
            }, 500);
          }
        };

        const achTranslations = {
          'ach.accountHolderNameField.title': 'Name on the account',
          'ach.bankAccountNumberVerification.label': 'Re-type account number',
        };
        const globalConfiguration = {
          session: adyenSessionConfig,
          environment: adyenSession.adyen.environment,
          amount: { value: amount, currency: 'USD' },
          locale: 'en-US',
          countryCode: 'US',
          clientKey: adyenSession.adyen.clientKey,
          showPayButton: false,
          translations: {
            'en-US': achTranslations,
            en_US: achTranslations,
            en: achTranslations,
          },
          onError: (error: any, component: any) => {
            let errorMessage = 'ERROR: Adyen ACH checkout error';
            setParentProcessing?.({ status: false });
            LogCannon.blast('Navigating to Error', {
              adyenComponent: component,
              appId,
              component: 'ECheckFormAdyen',
              errorMessage,
              error: typeof error === 'string' ? `${error}` : error,
              manifestId,
            });
            navigate(Pages.Error);
          },
        };

        const achConfiguration = {
          showPayButton: false,
          hasHolderName: true,
          enableStoreDetails: false,
          data: {
            selectedAccountType: ADYEN_ACH_DEFAULT_SELECTED_ACCOUNT_TYPE,
          } as AchConfiguration['data'],
          placeholders: {
            ownerName: '',
            routingNumber: '',
            accountNumber: '',
            accountNumberVerification: '',
          },
          amount: { value: 0, currency: 'USD' },
          onChange: (state: any) => {
            hideLoading();
            latestAchStateRef.current = state;
            setAchApiError('');
            const apply = () => {
              const s = latestAchStateRef.current;
              if (!s || !isMounted) return;
              syncButtonValidation(s);
            };
            apply();
            window.setTimeout(apply, 0);
          },
          styles: {
            base: {
              color: '#3D4043',
              fontSize: '16px',
              fontFamily: 'Roboto, Arial, sans-serif',
              fontWeight: '400',
            },
            error: { color: '#BE3317' },
          },
        };

        const checkout = await AdyenCheckout(globalConfiguration);
        if (!isMounted) return;
        ach = new Ach(checkout, achConfiguration).mount('#ach-container');
        setAchComponent(ach);
      } catch (error) {
        let errorMessage = 'ERROR: Failed to initialize Adyen ACH form';
        if (isMounted)
          LogCannon.blast('Navigating to Error', {
            appId,
            component: 'ECheckFormAdyen',
            errorMessage,
            error: typeof error === 'string' ? `${error}` : error,
            manifestId,
          });
        navigate(Pages.Error);
      }
    };

    init();
    return () => {
      isMounted = false;
      if (ach) {
        try {
          ach.unmount?.();
        } catch (e) {
          console.warn('Error unmounting Adyen ACH', e);
        }
      }
    };
  }, [adyenSessionConfig, adyenSession, amount, manifestId, navigate, setParentProcessing, syncButtonValidation]);

  useEffect(() => {
    if (!achComponent) return;
    return installAdyenMobileClearButtons({ containerId: 'ach-container' });
  }, [achComponent]);

  // Same name rules as ECheckFormAdyenAdyen “Name on the card” (useNameOnCardValidator on the account holder field).
  useEffect(() => {
    if (!achComponent) return;

    let isUnmounted = false;
    const timeouts: number[] = [];
    let removeListeners: (() => void) | undefined;

    const attachWhenOwnerNameInputReady = () => {
      if (isUnmounted) return;

      const ownerInput = document.querySelector('#ach-container input[name="ownerName"]') as HTMLInputElement | null;
      if (!ownerInput) {
        timeouts.push(window.setTimeout(attachWhenOwnerNameInputReady, 50));
        return;
      }

      const onInputCapture = (e: Event) => {
        const target = e.target as HTMLInputElement | null;
        if (!target) return;
        const cleaned = nameOnAccountValidator.validate({
          value: target.value,
          fieldProps: { label: 'Name on the account', required: true },
        }).value;
        if (cleaned !== target.value) {
          target.value = cleaned;
        }
      };

      const onKeyDown = (e: KeyboardEvent) => {
        if (e.ctrlKey || e.metaKey || e.altKey) return;
        if (e.key.length !== 1) return;
        const allowed = /[a-zA-Z\s.'‘’\-]/.test(e.key);
        if (!allowed) e.preventDefault();
      };

      ownerInput.addEventListener('input', onInputCapture, true);
      ownerInput.addEventListener('keydown', onKeyDown);

      removeListeners = () => {
        ownerInput.removeEventListener('input', onInputCapture, true);
        ownerInput.removeEventListener('keydown', onKeyDown);
      };
    };

    attachWhenOwnerNameInputReady();

    return () => {
      isUnmounted = true;
      timeouts.forEach((t) => clearTimeout(t));
      removeListeners?.();
    };
  }, [achComponent, nameOnAccountValidator]);

  // Re-sync Continue with account / re-type fields: Adyen `onChange` may not re-run on every keystroke
  // or may keep `isValid` true until blur while the two values differ.
  useEffect(() => {
    if (!achComponent) return;

    let isUnmounted = false;
    const timeouts: number[] = [];
    let removeListeners: (() => void) | undefined;

    const attachWhenAccountInputsReady = () => {
      if (isUnmounted) return;

      const root = document.getElementById('ach-container');
      const acc = root?.querySelector('input[name="accountNumber"]') as HTMLInputElement | null;
      const conf = root?.querySelector('input[name="accountNumberVerification"]') as HTMLInputElement | null;
      if (!acc || !conf) {
        timeouts.push(window.setTimeout(attachWhenAccountInputsReady, 50));
        return;
      }

      const onAccountFieldChange = () => {
        accountValueRef.current = acc.value.replace(/\D/g, '');
        retypeValueRef.current = conf.value.replace(/\D/g, '');
        const s = latestAchStateRef.current;
        if (s) syncButtonValidation(s);
      };

      const onAccFocus = () => {
        setAccountLengthError(null);
        setAccountRetypeMismatchError(null);
      };

      const onRetypeFocus = () => {
        setAccountRetypeMismatchError(null);
      };

      const onAccBlur = () => {
        const a = acc.value.replace(/\D/g, '');
        setAccountLengthError(a.length > 0 && a.length < 5 ? ACCOUNT_LENGTH_MIN_MESSAGE : null);
        syncMismatchError(latestAchStateRef.current);
      };

      const onConfBlur = () => {
        const s = latestAchStateRef.current;
        syncMismatchError(s);
      };

      acc.addEventListener('input', onAccountFieldChange);
      conf.addEventListener('input', onAccountFieldChange);
      acc.addEventListener('change', onAccountFieldChange);
      conf.addEventListener('change', onAccountFieldChange);
      acc.addEventListener('focus', onAccFocus);
      conf.addEventListener('focus', onRetypeFocus);
      acc.addEventListener('blur', onAccBlur);
      conf.addEventListener('blur', onConfBlur);

      removeListeners = () => {
        acc.removeEventListener('input', onAccountFieldChange);
        conf.removeEventListener('input', onAccountFieldChange);
        acc.removeEventListener('change', onAccountFieldChange);
        conf.removeEventListener('change', onAccountFieldChange);
        acc.removeEventListener('focus', onAccFocus);
        conf.removeEventListener('focus', onRetypeFocus);
        acc.removeEventListener('blur', onAccBlur);
        conf.removeEventListener('blur', onConfBlur);
      };
    };

    attachWhenAccountInputsReady();

    return () => {
      isUnmounted = true;
      timeouts.forEach((t) => clearTimeout(t));
      removeListeners?.();
    };
  }, [achComponent, syncButtonValidation, syncMismatchError]);

  // Account number field container for portaled length error.
  useEffect(() => {
    if (!achComponent) return;

    let cancelled = false;
    const tryFind = () => {
      if (cancelled) return;
      const root = document.getElementById('ach-container');
      const label = root?.querySelector('span.adyen-checkout__label__text[data-id="accountNumber"]');
      const el = label?.closest('.adyen-checkout__field') as HTMLElement | null;
      if (el) {
        setAccountNumberFieldEl(el);
        return;
      }
      window.setTimeout(tryFind, 50);
    };

    tryFind();
    return () => {
      cancelled = true;
      setAccountNumberFieldEl(null);
    };
  }, [achComponent]);

  useEffect(() => {
    if (!accountNumberFieldEl) return;
    if (accountLengthError) {
      accountNumberFieldEl.classList.add('adyen-checkout__field--error');
    } else {
      accountNumberFieldEl.classList.remove('adyen-checkout__field--error');
    }
  }, [accountNumberFieldEl, accountLengthError]);

  // Re-type field container for portaled "Account number does not match." (mirrors ECheckFormAdyenAdyen billing ZIP).
  useEffect(() => {
    if (!achComponent) return;

    let cancelled = false;
    const tryFind = () => {
      if (cancelled) return;
      const root = document.getElementById('ach-container');
      const label = root?.querySelector('span.adyen-checkout__label__text[data-id="accountNumberVerification"]');
      const el = label?.closest('.adyen-checkout__field') as HTMLElement | null;
      if (el) {
        setAccountRetypeFieldEl(el);
        return;
      }
      window.setTimeout(tryFind, 50);
    };

    tryFind();
    return () => {
      cancelled = true;
      setAccountRetypeFieldEl(null);
    };
  }, [achComponent]);

  useEffect(() => {
    if (!accountRetypeFieldEl) return;
    if (accountRetypeMismatchError) {
      accountRetypeFieldEl.classList.add('adyen-checkout__field--error');
    } else {
      accountRetypeFieldEl.classList.remove('adyen-checkout__field--error');
    }
  }, [accountRetypeFieldEl, accountRetypeMismatchError]);

  useLayoutEffect(() => {
    if (!accountRetypeMismatchError) {
      setShowPortaledRetypeMismatch(true);
      return;
    }
    if (!accountRetypeFieldEl) {
      return;
    }
    const el = accountRetypeFieldEl;
    const run = () => {
      if (!el.isConnected) return;
      setShowPortaledRetypeMismatch(!adyenHasNativeRetypeContextualError(el));
    };
    run();
    const mo = new MutationObserver(() => {
      run();
    });
    mo.observe(el, { childList: true, subtree: true, characterData: true });
    return () => {
      mo.disconnect();
    };
  }, [accountRetypeMismatchError, accountRetypeFieldEl, isFormValid]);

  const redirect = (url: string) => {
    setRedirected({ ...redirectMap, [manifestId]: url });
    navigate(`/redirect/${manifestId}`);
  };

  const handleSoftError = (error: { code: ErrorCode }) => {
    // to do: confirm soft errors are properly displayed once we have the api done.
    switch (error.code) {
      case ErrorCode.InvalidRoutingNumber:
        setAchApiError('Invalid routing number.');
        break;
      case ErrorCode.InvalidAccountNumber:
        setAchApiError('Invalid account number.');
        break;
      case ErrorCode.StorePaymentError:
        setAchApiError('This account number is invalid. Please try another payment method.');
        break;
      case ErrorCode.NetworkTimeout:
        if (appId === 'manage-saved-payments') {
          setAchApiError('Your payment method could not be saved. Please try again.');
        } else {
          setShowModal(true);
        }
        break;
      default:
        setShowModal(true);
        break;
    }
  };

  useEffect(() => {
    if (softErrorCode) {
      handleSoftError({ code: softErrorCode });
    }
  }, [softErrorCode]);

  const handleTokenOnlySubmit = async (payload: IAdyenEncryptedAchData) => {
    const sessionId = adyenSessionConfig?.id || '';
    const tokenPayload: ISubmitAdyenTokenRequest = {
      adyen: {
        sessionId,
        achData: {
          ownerName: payload.ownerName,
          accountNumber: payload.accountNumber,
          routingNumber: payload.routingNumber,
          bankAccountType: payload.bankAccountType,
          accountHolderType: payload.accountHolderType,
          last4: payload.last4,
        },
      },
    };

    LogCannon.blast('Submitting Adyen ACH token (tokenOnly)', { sessionId });

    try {
      setProcessing(true);

      const response = await sources.submitAdyenToken(sessionId, tokenPayload);
      LogCannon.blast('eCheck - Adyen token submission successful', { response });

      setParentProcessing?.({
        status: true,
        text: '',
        cindy: false,
        displayCheckmark: true,
        willRedirect: true,
        callback: () => {
          window.location.href = returnUrl;
        },
      });
    } catch (error: any) {
      const errorMessage = 'ERROR: Submit Adyen ACH token (tokenOnly) Exception';
      LogCannon.blast('Navigating to Error', {
        appId,
        component: 'ECheckFormAdyen',
        errorMessage,
        error: typeof error === 'string' ? `${error}` : error,
        manifestId,
      });
      navigate(Pages.Error);
    }
  };

  const submitPayment = async (payload: IAdyenEncryptedAchData) => {
    // Match ECheckFormAdyen: fresh submit attempt clears the top soft-error alert (e.g. after a failed call from review).
    setAchApiError('');
    setPaymentStatusUnknown(false);

    try {
      const isManifestStale = !!manifestTimestamp && Date.now() - manifestTimestamp > MANIFEST_TTL_MS;
      if (isManifestStale) {
        throw { type: ErrorType.SESSION_TIMEOUT, message: 'Manifest is stale' };
      }
      setParentProcessing?.({
        status: true,
        text: '',
        cindy: false,
        displayCheckmark: false,
      });
      setProcessing(true);

      const sessionId = adyenSessionConfig?.id || '';
      const processPaymentBody: IProcessPaymentWithManifestRequest = {
        manifestId,
        paymentType: PaymentType.Echeck,
        scheduledDate,
        adyenData: {
          sessionId,
          achData: {
            ownerName: payload.ownerName,
            accountNumber: payload.accountNumber,
            routingNumber: payload.routingNumber,
            bankAccountType: payload.bankAccountType,
            accountHolderType: payload.accountHolderType,
            last4: payload.last4 || payload.accountNumber.substring(payload.accountNumber.length - 4) || '',
          },
        },
      };

      if ((allowStoredPayment && storePaymentChecked) || appId === 'manage-saved-payments') {
        processPaymentBody.storePayment = { enable: true };
      }

      setPaymentProcessing(true);
      const { transactionId } = await ProcessPaymentHelper.processPayment(processPaymentBody);
      setParentProcessing?.({
        status: true,
        text: '',
        cindy: false,
        displayCheckmark: true,
        callback: () => {
          const returnUrlWithTransactionId = UrlHelper.addQueryParam(returnUrl, { transactionId });
          redirect(returnUrlWithTransactionId);
        },
      });
    } catch (error: any) {
      setParentProcessing?.({
        status: true,
        text: '',
        cindy: false,
        displayCheckmark: true,
        callback: () => {
          setProcessing(false);
        },
      });
      if (error?.type === ErrorSeverity.SOFT && error?.message === 'payment_status_unknown') {
        setProcessing(false);
        setPaymentReview(false);
        LogCannon.blast('Payment status unknown after Adyen echeck payment — showing inline message', {
          appId,
          component: 'ECheckFormAdyen',
          manifestId,
        });
        setPaymentStatusUnknown(true);
      } else if (error.type === ErrorSeverity.SOFT) {
        setProcessing(false);
        setPaymentReview(false);
        handleSoftError(error);
      } else {
        const isExpired = error?.type === ErrorType.SESSION_TIMEOUT;
        let errorMessage = 'ERROR: Submit Adyen ECheck Payment Exception';

        if (error.code === ErrorCode.TooManyRequests) {
          errorMessage = 'ERROR: Submit Adyen ECheck Payment Rate Limited Exception';
          setError?.({ errorType: ErrorType.RATE_LIMITED, errorMessage: '' });
        } else if (isExpired) {
          setError?.({ errorType: ErrorType.SESSION_TIMEOUT, errorMessage: error?.message ?? '' });
        }
        LogCannon.blast(isExpired ? 'Navigating to Session Error' : 'Navigating to Error', {
          appId,
          component: 'ECheckFormAdyen',
          errorMessage,
          error: typeof error === 'string' ? `${error}` : error,
          manifestId,
          ...(isExpired && { manifestExpiredAfterMs: Date.now() - manifestTimestamp!, expiredManifestId: manifestId }),
        });
        navigate(Pages.Error);
      }
    }
  };

  const renderPaymentReview = manifest?.clientConfig?.renderPaymentReview;

  const handleContinue = () => {
    const state = achComponent?.state ?? latestAchStateRef.current;
    if (!state) return;
    if (achAccountMismatch(state)) return;
    if (!state.isValid) return;
    const payload = capturedAchData || achPayloadFromPaymentMethod(state?.data?.paymentMethod);
    if (!payload) return;
    setReviewPayload(payload);
    // Match ECheckFormAdyenAdyen: clear the prior soft/API banner when committing to a new review step or charge (not on early validation exits).
    setAchApiError('');

    if (renderPaymentReview && amount > 0) {
      setPaymentReview(true);
    } else if (tokenOnly) {
      void handleTokenOnlySubmit(payload);
    } else {
      void submitPayment(payload);
    }
  };

  const modalDialog = (
    <Modal
      open={showModal && !isProcessing}
      onClose={() => setShowModal(false)}
      variants={{ xs: 'bottom-sheet', sm: 'regular' }}
    >
      <ModalTitle id="error-modal-title-echeck-adyen" sx={{ marginRight: '2rem' }}>
        Please try again.
        <ModalCloseIconButton aria-label="Close" onClick={() => setShowModal(false)} />
      </ModalTitle>
      <ModalContent id="error-modal-content-echeck-adyen">
        <Typography variant="b1Regular">There was an unexpected error.</Typography>
      </ModalContent>
      <ModalActions actionLayout={{ sm: 'fullWidth', md: 'fullWidth', lg: 'rightAlign', xl: 'rightAlign' }}>
        <ModalActionButton label="OK" onClick={() => setShowModal(false)} />
      </ModalActions>
    </Modal>
  );

  const echeckModal = (
    <Modal
      open={showEcheckModal}
      onClose={() => setShowEcheckModal(false)}
      variants={{ xs: 'bottom-sheet', sm: 'regular' }}
    >
      <ModalTitle id="modal-title-echeck-adyen" sx={{ marginRight: '2rem' }}>
        Where do I find this?
        <ModalCloseIconButton aria-label="Close" onClick={() => setShowEcheckModal(false)} />
      </ModalTitle>
      <ModalContent id="modal-content-echeck-adyen">
        <Typography variant="b1Regular">
          You can find your routing and account number at the bottom of your checks. The first set of numbers are the
          routing number, and the second set is your account number.
        </Typography>
        <div className="echeck-img-div">
          <img src={echeckImage} className="echeck-img" alt="echeck" />
        </div>
      </ModalContent>
      <ModalActions actionLayout={{ sm: 'fullWidth', md: 'fullWidth', lg: 'rightAlign', xl: 'rightAlign' }}>
        <ModalActionButton label="Got it" onClick={() => setShowEcheckModal(false)} />
      </ModalActions>
    </Modal>
  );

  const handleStorePaymentCheck = () => setStorePaymentChecked((sp) => !sp);

  const isScheduledPayment: boolean = !!isScheduled || !!manifest?.isScheduled;
  const authText = isScheduledPayment || !(amount > 0) || renderPaymentReview ? 'Continue' : 'Pay now';
  const isMap = appId === 'manage-autopay';
  const isStorePaymentFlow = amount === 0 && !autoPay;
  const mapChangeFlow = isMap && manifest?.renderTsAndCs?.render;
  const mapNewEnrollFlow = isMap && !manifest?.renderTsAndCs?.render;
  const renderTermsAndPaymentAuth = mapChangeFlow && paymentMethodsSkipped;
  const willVisitPaymentReview = renderPaymentReview && amount > 0;
  const renderPaymentAuth =
    !renderTermsAndPaymentAuth &&
    ((mapNewEnrollFlow && amount === 0) || mapChangeFlow || !(willVisitPaymentReview || isStorePaymentFlow));

  const handleBack = () => {
    navigatePaymentFormBack(navigate, manifest, {
      softErrorCode,
      paymentMethodsSkipped,
      onClearPaymentMethodsSkipped: () => setpaymentMethodsSkipped(false),
    });
  };

  const amountDue = amount > 0;
  const buttonText = !isScheduled && amountDue && !renderPaymentReview ? 'Pay now' : 'Continue';
  const disabled = !isFormValid || isLoadingSession;

  return (
    <>
      {isPaymentReview && !isProcessing && (
        <PaymentReview
          amount={manifest?.amount!}
          paymentDetails={{
            paymentType: PaymentType.Echeck,
            echeck: {
              last4,
              routingNumber,
            },
          }}
          scheduledDate={scheduledDate}
          pit={manifest?.pit ? { ...manifest.pit } : undefined}
          onSubmit={() => {
            if (reviewPayload) {
              if (tokenOnly) {
                handleTokenOnlySubmit(reviewPayload);
              } else {
                submitPayment(reviewPayload);
              }
            }
          }}
          onBack={() => {
            // Match ECheckFormAdyen: leaving review clears stale API messages so the ACH form is not still showing a prior pay failure.
            setAchApiError('');
            setPaymentReview(false);
            const s = latestAchStateRef.current;
            if (s) {
              syncButtonValidation(s);
            }
          }}
        />
      )}

      <div
        className="payment-form"
        style={
          isLoadingSession || isProcessing || isPaymentReview || hideWhileParentProcessing ? { display: 'none' } : {}
        }
      >
        <div className="payment-form-cream-section">
          <div className="payment-form-body">
            <Header />
            {!isMobileApp && <AppHeaderBackButton id="back-button" onClick={handleBack} />}
            <Box className="secure-checkout">
              <Icon iconName="lock-keyhole-solid" className="lock-icon" />
              <span>Secure checkout</span>
            </Box>
            <PaymentHeader paymentType={PaymentType.Echeck} amount={amount} autoPay={autoPay} />
            <Typography variant="b1Regular" data-quid="echeck-form-subtitle">
              {paymentFormSubtitle}
            </Typography>
          </div>
        </div>
        <div className="payment-form-body echeck-form">
          <Box id="echeck-form-adyen-inner-container">
            {paymentStatusUnknown && (
              <Alert severity="warning" id="payment-status-unknown-alert" displayCloseButton={false}>
                <AlertTypography>
                  Payment submitted but there was an issue confirming it — please check{' '}
                  <Link href={getMyAccountURL()} target="_blank" rel="noopener noreferrer" >
                    My Account
                  </Link>{' '}
                  before retrying to avoid a duplicate charge.
                </AlertTypography>
              </Alert>
            )}
            {achApiError && (
              <Alert severity="error" id="echeck-adyen-soft-error" displayCloseButton={false}>
                <AlertTypography>{achApiError}</AlertTypography>
              </Alert>
            )}
            <div id="ach-container" />
            {accountNumberFieldEl &&
              accountLengthError &&
              createPortal(
                <div className="adyen-checkout-contextual-text adyen-checkout-contextual-text--error">
                  {accountLengthError}
                </div>,
                accountNumberFieldEl
              )}
            {accountRetypeFieldEl &&
              accountRetypeMismatchError &&
              showPortaledRetypeMismatch &&
              createPortal(
                <div
                  className="adyen-checkout-contextual-text adyen-checkout-contextual-text--error"
                  data-ach-retype-mismatch="true"
                >
                  {accountRetypeMismatchError}
                </div>,
                accountRetypeFieldEl
              )}
            {allowStoredPayment && (
              <CheckboxGroup className="save-this-payment" variant="text">
                <Checkbox
                  name="store-payment"
                  checked={storePaymentChecked}
                  label={<CheckboxTypography>Save for future payments</CheckboxTypography>}
                  enableForm={false}
                  onClick={() => handleStorePaymentCheck()}
                />
              </CheckboxGroup>
            )}
            {renderPaymentAuth && (
              <Typography variant="b1Regular" data-quid="payment-authorization-text">
                By clicking {authText}, I hereby state that I am an authorized user for the above payment method, and
                have read and agree to the{' '}
                <Link
                  id="payment-authorization-link"
                  component="button"
                  sx={{ verticalAlign: 'baseline' }}
                  onClick={(e: any) => {
                    setShowPaymentAuthorization(true);
                    e.preventDefault();
                  }}
                  variant="b1Regular"
                >
                  Payment Authorization
                </Link>
                .
              </Typography>
            )}
            {renderTermsAndPaymentAuth && (
              <Typography variant="b2Regular" className="terms-and-conditions">
                By clicking Continue, I hereby enroll in Auto Pay and that I am an authorized user for the above payment
                method I have selected, and have read and agree to the{' '}
                <Link
                  data-id="terms-conditions-link"
                  id="terms-conditions-link"
                  variant="b2Regular"
                  href={getTermsUrl()}
                  target="_blank"
                  sx={{ verticalAlign: 'baseline' }}
                >
                  Terms & Conditions
                </Link>{' '}
                <Typography variant="b2Regular" sx={{ display: 'inline-block' }}>
                  and{' '}
                </Typography>
                <Link
                  data-id="terms-conditions-link"
                  variant="b2Regular"
                  component="button"
                  sx={{ verticalAlign: 'baseline' }}
                  onClick={(e: any) => {
                    setShowPaymentAuthorization(true);
                    e.preventDefault();
                  }}
                >
                  Payment Authorization
                </Link>
                .
              </Typography>
            )}
            {showPaymentAuthorization && (
              <PaymentMethodsAuthorizationModal
                handleClose={() => setShowPaymentAuthorization(false)}
                ctaText={authText}
              />
            )}
          </Box>
          <StickyButton
            id="submit-echeck-adyen"
            slotProps={{
              primaryButton: {
                label: buttonText,
                onClick: handleContinue,
                disabled,
                endIcon: amountDue ? <Icon iconName="lock-keyhole-solid" /> : undefined,
              },
            }}
            renderAmount={amountDue ? amount : undefined}
          />
        </div>
        {modalDialog}
        {echeckModal}
      </div>
    </>
  );
};

export default ECheckFormAdyen;
